import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Alert, 
  Paper, 
  Divider, 
  Card, 
  CardContent,
  useTheme,
  alpha,
  CircularProgress,
  Fade
} from '@mui/material';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, Globe } from 'lucide-react';
import emailjs from 'emailjs-com';
import { useLocation } from 'react-router-dom';

const SERVICE_ID = 'service_8xd2ccb';
const TEMPLATE_ID = 'template_i346kzp';
const PUBLIC_KEY = 'Xh131ee4HN8vrNER2';

interface FormData extends Record<string, unknown> {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface LocationState {
  subject?: string;
}

const ContactPage: React.FC = () => {
  const location = useLocation();
  const locationState = location.state as LocationState;
  const theme = useTheme();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [status, setStatus] = useState<'success' | 'error' | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Check for subject in location state when component mounts
  useEffect(() => {
    if (locationState && locationState.subject) {
      setFormData(prevData => ({
        ...prevData,
        subject: locationState.subject || ''
      }));
    }
  }, [locationState]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, formData, PUBLIC_KEY);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 5, 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="div"
            sx={{ 
              color: 'primary.main',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: 1.5,
              mb: 2,
              textTransform: 'uppercase'
            }}
          >
            Contact Us
          </Typography>
          <Divider sx={{ width: '80px', mx: 'auto', mb: 3, borderWidth: 2, borderColor: 'primary.main' }} />
        </Box>

        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            textAlign: 'center', 
            maxWidth: '800px', 
            mx: 'auto',
            fontSize: '1.1rem',
            lineHeight: 1.6
          }}
        >
          We'd love to hear from you! Whether you would like to schedule a demo or have a question about features,
          pricing, or anything else, our team is ready to answer all your questions.
        </Typography>
      </Paper>

      <Grid container spacing={4}>
        {/* Contact Form Section */}
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              height: '100%', 
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '8px', 
                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              }} 
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <MessageSquare size={24} style={{ color: theme.palette.primary.main, marginRight: '12px' }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Send us a message
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 4 }} />

            <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={6}
                    fullWidth
                    required
                    variant="outlined"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large" 
                    type="submit" 
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                    sx={{ 
                      mt: 1,
                      py: 1.5,
                      px: 4,
                      borderRadius: '30px',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                      }
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            <Fade in={status !== null}>
              <Box sx={{ mt: 3 }}>
                {status === 'success' && (
                  <Alert 
                    severity="success" 
                    variant="filled"
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: 2
                    }}
                  >
                    Your message has been sent successfully! We'll get back to you soon.
                  </Alert>
                )}
                {status === 'error' && (
                  <Alert 
                    severity="error" 
                    variant="filled"
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: 2
                    }}
                  >
                    There was an error sending your message. Please try again later.
                  </Alert>
                )}
              </Box>
            </Fade>
          </Paper>
        </Grid>

        {/* Contact Information Section */}
        <Grid item xs={12} md={5}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                width: '100%', 
                height: '8px', 
                background: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`
              }} 
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Mail size={24} style={{ color: theme.palette.secondary.main, marginRight: '12px' }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Our Information
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card 
                  elevation={1} 
                  sx={{ 
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        p: 1.5,
                        mr: 2
                      }}
                    >
                      <Mail size={24} style={{ color: theme.palette.primary.main }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        info@recruitmentintelligence.com
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card 
                  elevation={1} 
                  sx={{ 
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                        borderRadius: '50%',
                        p: 1.5,
                        mr: 2
                      }}
                    >
                      <Phone size={24} style={{ color: theme.palette.secondary.main }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        +1 (954) 342-0550
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card 
                  elevation={1} 
                  sx={{ 
                    borderRadius: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: '50%',
                        p: 1.5,
                        mr: 2,
                        mt: 0.5
                      }}
                    >
                      <MapPin size={24} style={{ color: theme.palette.success.main }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        2200 North Commerce Pkwy STE 200, 
                        <br />Weston, FL 33326, USA
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Business Hours Box - Now at the bottom of contact information */}
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3, 
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: 'white'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Business Hours
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Clock size={18} style={{ marginRight: '8px', opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Monday - Friday
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500, ml: 3.5 }}>
                  9:00 AM - 6:00 PM
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Globe size={18} style={{ marginRight: '8px', opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Time Zone
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 500, ml: 3.5 }}>
                  Eastern Time (ET)
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContactPage;
